import {
  ENDPOINTS,
  SearchAliasListRequestSchema,
  SearchAliasMineRequestSchema,
  type SearchAliasListRequest,
  type SearchAliasListResponse,
  type SearchAliasMineResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';

/**
 * `search-aliases` and `search-aliases-mine` — the two calls behind the Aliases
 * panel on Search quality (CLM-59, FR-26). Alias *writes* go through the
 * existing `manageClause` client (`./clauses`): `create_search_alias`,
 * `approve_search_alias` and `set_search_alias_active` are members of the
 * ratified `manage-clause` union, so there is nothing to add here for them.
 *
 * `clauses.ts` is the precedent: the path comes from the registry, the request
 * is `.parse`d before it leaves the browser, and the response is **parsed**
 * rather than asserted, so an off-contract answer is a `ZodError` here and never
 * data on screen. The list is a GET with dotted keys for the nested page object
 * (`page.limit`, `page.cursor`), the way `apps/api/src/http/decode.ts` reads them.
 */

const LIST_ENDPOINT = ENDPOINTS['search-aliases'];
const MINE_ENDPOINT = ENDPOINTS['search-aliases-mine'];

/** Flatten a validated list request into the query the decoder expands. */
export function searchAliasListQuery(request: SearchAliasListRequest): Query {
  const query: Record<string, string | number> = {};
  if (request.status !== undefined) query['status'] = request.status;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function listSearchAliases(
  request: SearchAliasListRequest,
  signal?: AbortSignal,
): Promise<SearchAliasListResponse> {
  // `.strict()`, so an invented or scope-shaped key is a ZodError here rather
  // than a parameter on the wire.
  const validated = SearchAliasListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(LIST_ENDPOINT.path, {
    query: searchAliasListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return LIST_ENDPOINT.response.parse(raw);
}

/**
 * `POST /v1/search/aliases/mine` with the empty body the contract fixes — the
 * window, floor, threshold and cap are the API's environment, so a click cannot
 * lower the bar. A governance write: never called on render, only on the button.
 */
export async function mineSearchAliases(signal?: AbortSignal): Promise<SearchAliasMineResponse> {
  const body = SearchAliasMineRequestSchema.parse({});
  const raw = await apiPost<unknown>(MINE_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return MINE_ENDPOINT.response.parse(raw);
}
